import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, BellRing, WifiOff } from 'lucide-react';
import { Modal, Button, Carousel } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { useHistory } from 'react-router-dom';
import useNotifications from '../../hooks/DataFetching/useNotifications';
import usePushNotifications from '../../hooks/usePushNotifications';
import { getNotificationTarget } from '../../lib/notificationTargets';
import '../../styles/components/NotificationBell.css';

export const NotificationBell = ({
    triggerClassName,
    triggerLabel,
    dropdownPlacement = 'top',
}) => {
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();
    const {
        notifications,
        notificationError,
        isLoadingNotifications,
        markAsRead,
        fetchNotifications,
        updateTradeStatus,
        updateFriendRequest,
        isConnected
    } = useNotifications();
    const push = usePushNotifications();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownPanelRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState(null);
    const history = useHistory();
    const apiUrl = process.env.REACT_APP_API_URL;

    const getTopicIsChat = useCallback(async (topicId) => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
            });

            const response = await fetch(`${apiUrl}/api/topics/${topicId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                return false;
            }

            const topic = await response.json();
            const flag = topic?.isChat ?? topic?.IsChat;
            return flag === true || flag === "True" || flag === "true" || flag === 1;
        } catch (error) {
            console.error("Failed to fetch topic for notification target", error);
            return false;
        }
    }, [apiUrl, getAccessTokenSilently]);

    const handleNavigateToMessage = useCallback(async (notification) => {
        const topicId = notification?.tradeId;
        const postId = notification?.foreignKey;

        if (!topicId || !postId) {
            return;
        }

        const isChatTopic = await getTopicIsChat(topicId);

        const searchParams = new URLSearchParams();
        searchParams.set('topicId', topicId);
        searchParams.set('postId', postId);
        if (isChatTopic) {
            searchParams.set('view', 'chat');
        }

        history.push(`/forum?${searchParams.toString()}`);
    }, [getTopicIsChat, history]);

    const updateDropdownPosition = useCallback(() => {
        if (!triggerRef.current || typeof window === 'undefined') return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportPadding = 12;
        const panelWidth = Math.min(368, window.innerWidth - viewportPadding * 2);
        const left = Math.min(
            Math.max(viewportPadding, triggerRect.right - panelWidth),
            window.innerWidth - panelWidth - viewportPadding,
        );
        const top = dropdownPlacement === 'bottom-nav'
            ? Math.max(viewportPadding, triggerRect.top - 12)
            : Math.min(window.innerHeight - viewportPadding, triggerRect.bottom + 12);

        setDropdownPosition({
            left,
            top,
            width: panelWidth,
            transform: dropdownPlacement === 'bottom-nav' ? 'translateY(-100%)' : 'none',
        });
    }, [dropdownPlacement]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            if (
                rootRef.current
                && !rootRef.current.contains(target)
                && dropdownPanelRef.current
                && !dropdownPanelRef.current.contains(target)
            ) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!dropdownOpen) {
            setDropdownPosition(null);
            return undefined;
        }

        updateDropdownPosition();
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);

        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [dropdownOpen, updateDropdownPosition]);

    const handleNotificationClick = (notification) => {
        if (!notification) return;

        const shouldOpenModal = notification.notificationType === 'tradeoffer' || notification.notificationType === 'friendrequest';

        if (!notification.isRead) {
            markAsRead(notification.id);
        }

        setDropdownOpen(false);

        if (notification.notificationType === 'messagereply' || notification.notificationType === 'chatmessage') {
            handleNavigateToMessage(notification);
            return;
        }

        if (shouldOpenModal) {
            setSelectedNotification(notification);
            setModalIsOpen(true);
            return;
        }

        history.push(getNotificationTarget(notification));
    };

    const handleCloseModal = () => {
        setModalIsOpen(false);
        setSelectedNotification(null);
    };

    const handleAcceptTrade = async () => {
        await updateTradeStatus(selectedNotification.tradeId, 'accept');
        handleCloseModal();
    };

    const handleRejectTrade = async () => {
        await updateTradeStatus(selectedNotification.tradeId, 'reject');
        handleCloseModal();
    };

    const handleAcceptFriendRequest = async () => {
        await updateFriendRequest(selectedNotification.foreignKey, 'accept');
        handleCloseModal();
    };

    const handleRejectFriendRequest = async () => {
        await updateFriendRequest(selectedNotification.foreignKey, 'reject');
        handleCloseModal();
    };

    const handleToggleDropdown = async () => {
        const nextOpen = !dropdownOpen;
        setDropdownOpen(nextOpen);

        if (nextOpen) {
            await fetchNotifications();
        }
    };

    const unreadCount = notifications.filter(n => !n.isRead).length;
    const displayCount = unreadCount > 99 ? '99+' : unreadCount;
    const defaultTriggerClassName = "relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/20 text-primary-foreground transition hover:bg-primary/30 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background";
    const resolveFriendRequestSender = (notification) => {
        if (!notification) return null;
        const directName = notification.senderName
            || notification.requesterName
            || notification.fromUserName
            || notification.fromUser
            || notification.userName;
        if (directName) {
            return directName;
        }

        const candidates = [notification.content, notification.title].filter(Boolean);
        const fromRegex = /from\s+([^.!?]+)/i;
        const sentRegex = /^([^.!?]+?)\s+sent\s+you/i;

        for (const text of candidates) {
            const fromMatch = text.match(fromRegex);
            if (fromMatch?.[1]) {
                return fromMatch[1].trim();
            }
            const sentMatch = text.match(sentRegex);
            if (sentMatch?.[1]) {
                return sentMatch[1].trim();
            }
        }

        return null;
    };

    const friendRequestSender = resolveFriendRequestSender(selectedNotification);
    const friendRequestTitle = friendRequestSender
        ? `Friend request from ${friendRequestSender}`
        : (selectedNotification?.title || "New friend request");
    const friendRequestDetail = selectedNotification?.content
        && selectedNotification?.content !== selectedNotification?.title
        ? selectedNotification.content
        : null;
    const dropdownPanel = dropdownOpen && typeof document !== 'undefined' ? createPortal(
        <div
            ref={dropdownPanelRef}
            className={`notification-dropdown notification-dropdown--floating fixed rounded-md border border-primary/30 bg-card text-card-foreground shadow-xl ${dropdownPlacement === 'bottom-nav' ? 'notification-dropdown--bottom-nav' : ''}`}
            style={dropdownPosition || undefined}
        >
            <div className="flex items-center justify-between border-b border-primary/30 px-3 py-2 text-sm font-semibold">
                <span>Notifications</span>
                {!isConnected && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
                        Offline
                    </span>
                )}
            </div>
            <div className="notification-push-card">
                <div className="notification-push-copy">
                    <span className="notification-push-eyebrow">Mobile alerts</span>
                    <strong>
                        {push.enabled ? 'Push notifications are on' : 'Never miss an important update'}
                    </strong>
                    <span>
                        {push.enabled
                            ? 'TYK can alert this device when new notifications arrive.'
                            : push.configured
                                ? 'Enable browser push for offers, replies, requests, and group updates.'
                                : 'Push delivery needs VAPID keys before this environment can send alerts.'}
                    </span>
                    {push.error && <span className="notification-push-error">{push.error}</span>}
                </div>
                {push.enabled ? (
                    <button
                        type="button"
                        className="notification-push-action secondary"
                        onClick={push.disablePush}
                        disabled={push.isSaving}
                    >
                        Off
                    </button>
                ) : (
                    <button
                        type="button"
                        className="notification-push-action"
                        onClick={push.enablePush}
                        disabled={push.isSaving || !push.supported || !isAuthenticated}
                    >
                        <BellRing className="h-4 w-4" aria-hidden="true" />
                        {push.isSaving ? 'Saving' : 'Enable'}
                    </button>
                )}
            </div>
            <div className="max-h-80 overflow-y-auto">
                {isLoadingNotifications ? (
                    <p className="px-3 py-4 text-sm text-muted-foreground">Loading notifications...</p>
                ) : notificationError ? (
                    <div className="notification-error" role="alert">
                        <p>{notificationError}</p>
                        <button type="button" onClick={fetchNotifications}>Try again</button>
                    </div>
                ) : notifications.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-muted-foreground">No notifications yet.</p>
                ) : (
                    notifications.map(notification => (
                        <button
                            key={notification.id}
                            className={`flex w-full gap-3 px-3 py-3 text-left transition hover:bg-muted/40 ${notification.isRead ? 'opacity-70' : ''}`}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <span className={`mt-1 h-2.5 w-2.5 rounded-full ${notification.isRead ? 'bg-muted-foreground/50' : 'bg-primary'}`} />
                            <div className="flex-1 overflow-hidden">
                                <p className="truncate text-sm font-semibold">{notification.title}</p>
                                {notification.content && (
                                    <p className="truncate text-xs text-muted-foreground">{notification.content}</p>
                                )}
                            </div>
                            <span className="text-[10px] text-muted-foreground">
                                {notification.createdAt ? new Date(notification.createdAt).toLocaleDateString() : ''}
                            </span>
                        </button>
                    ))
                )}
            </div>
        </div>,
        document.body,
    ) : null;

    return (
        <div className="notification-bell-root relative" ref={rootRef}>
            <button 
                ref={triggerRef}
                className={triggerClassName || defaultTriggerClassName}
                onClick={handleToggleDropdown}
                aria-expanded={dropdownOpen}
                aria-label="Notifications"
                type="button"
            >
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex min-w-[1.5rem] items-center justify-center rounded-full bg-red-600 px-1 text-xs font-semibold text-white">
                        {displayCount}
                    </span>
                )}
                <Bell className="h-5 w-5" aria-hidden="true" />
                {triggerLabel && <span>{triggerLabel}</span>}
            </button>

            {dropdownPanel}

            {selectedNotification?.notificationType === 'tradeoffer' && (
                <Modal
                    show={modalIsOpen}
                    onHide={handleCloseModal}
                    size="lg"
                    centered
                    className="tyk-modal"
                >
                    <Modal.Header closeButton closeVariant="white">
                        <div>
                            <span className="tyk-kicker">Trade offer</span>
                            <Modal.Title>{selectedNotification ? selectedNotification.title : "Notification Details"}</Modal.Title>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedNotification && (
                            <div className="tyk-panel space-y-3">
                                <p className="text-sm text-slate-200">{selectedNotification.content}</p>
                                {selectedNotification.notificationType === 'tradeoffer' && (
                                    <div className="space-y-3">
                                        <h3>Items Offered</h3>
                                        {Array.isArray(selectedNotification.itemsOffered) && selectedNotification.itemsOffered.length > 0 ? (
                                            <Carousel>
                                                {selectedNotification.itemsOffered.map((item, index) => (
                                                    <Carousel.Item key={index}>
                                                        <img
                                                            className="d-block w-100"
                                                            src={item}
                                                            style={{ maxHeight: '300px', objectFit: 'contain' }}
                                                            alt="Item" 
                                                        />
                                                        <Carousel.Caption>
                                                            <h3>{item.assetName}</h3>
                                                        </Carousel.Caption>
                                                    </Carousel.Item>
                                                ))}
                                            </Carousel>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">No items attached to this offer.</p>
                                        )}
                                        <div>
                                            <h3>Item Requested</h3>
                                            {selectedNotification.itemRequested ? (
                                                <img
                                                    className="d-block w-100"
                                                    src={selectedNotification.itemRequested}
                                                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                                                    alt="Requested Item" 
                                                />
                                            ) : (
                                                <p className="text-sm text-muted-foreground">No requested item attached.</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
                        {selectedNotification && (
                            <>
                                <Button variant="primary" onClick={handleAcceptTrade}>Accept</Button>
                                <Button variant="danger" onClick={handleRejectTrade}>Reject</Button>
                            </>
                        )}
                    </Modal.Footer>
                </Modal>
            )}

            {selectedNotification?.notificationType === 'friendrequest' && (
                <Modal
                    show={modalIsOpen}
                    onHide={handleCloseModal}
                    size="lg"
                    centered
                    className="tyk-modal"
                    dialogClassName="friend-request-notification-modal"
                    contentClassName="friend-request-notification-modal-content"
                >
                    <Modal.Header closeButton closeVariant="white">
                        <Modal.Title>
                            <span className="friend-request-modal-title">{friendRequestTitle}</span>
                            <span className="friend-request-modal-tag">New</span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedNotification && (
                            <div className="friend-request-modal-body">
                                <p className="friend-request-modal-lede">
                                    {friendRequestSender
                                        ? <>You&apos;ve got a new request from <span className="friend-request-modal-sender">{friendRequestSender}</span>.</>
                                        : <>You&apos;ve got a new friend request waiting for your response.</>
                                    }
                                </p>
                                {friendRequestDetail && (
                                    <p className="friend-request-modal-detail">{friendRequestDetail}</p>
                                )}
                                <div className="friend-request-modal-note">
                                    Accept to add them to your circle, or decline if you don&apos;t recognize them.
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="friend-request-modal-actions">
                        <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
                        {selectedNotification && (
                            <>
                                <Button variant="primary" onClick={handleAcceptFriendRequest}>Accept</Button>
                                <Button variant="outline-light" onClick={handleRejectFriendRequest}>Decline</Button>
                            </>
                        )}
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};
